/**
 * Vendor Data Loader Service
 *
 * Loads vendor data from JSON files and extracts business categories from names.
 * Used as fallback when API is unavailable.
 */

import type { Vendor } from '../types/shared';

/**
 * Category extraction patterns
 * Maps keyword patterns to business categories
 */
const CATEGORY_PATTERNS: Array<{ keywords: RegExp; category: string }> = [
  { keywords: /electric/i, category: 'Electrician' },
  { keywords: /plumb/i, category: 'Plumber' },
  { keywords: /hvac|heating|cooling|air condition/i, category: 'HVAC' },
  { keywords: /landscape|lawn|garden/i, category: 'Landscaper' },
  { keywords: /handyman|repair|fix/i, category: 'Handyman' },
  { keywords: /paint/i, category: 'Painter' },
  { keywords: /roof/i, category: 'Roofer' },
  { keywords: /pool|spa/i, category: 'Pool Cleaner' },
  { keywords: /carpet|clean/i, category: 'Carpet Cleaner' },
  { keywords: /pest|termite/i, category: 'Pest Control' },
  { keywords: /window/i, category: 'Window Cleaner' },
  { keywords: /gutter/i, category: 'Gutter Cleaner' },
  { keywords: /garage|door/i, category: 'Garage Door' },
  { keywords: /fence/i, category: 'Fencing' },
  { keywords: /concrete|driveway/i, category: 'Concrete' },
  { keywords: /mason|brick/i, category: 'Masonry' },
  { keywords: /siding/i, category: 'Siding' },
  { keywords: /deck|patio/i, category: 'Deck & Patio' },
];

/**
 * Extract business category from vendor name
 *
 * @param vendor - Vendor object with name property
 * @returns Extracted category string
 *
 * @example
 * extractCategory({ name: 'ABC Electric Services' }) // 'Electrician'
 * extractCategory({ name: "Joe's Plumbing" }) // 'Plumber'
 * extractCategory({ name: 'Random Business LLC' }) // 'Other'
 */
export function extractCategory(vendor: { name: string }): string {
  const name = vendor.name.toLowerCase();

  for (const { keywords, category } of CATEGORY_PATTERNS) {
    if (keywords.test(name)) {
      return category;
    }
  }

  return 'Other';
}

/**
 * Parse rating string from JSON data
 *
 * JSON category field contains rating like "4.7(11)" or "No reviews"
 * Extract numeric rating and review count
 *
 * @param ratingStr - Rating string from JSON
 * @returns Object with rating and reviewCount
 */
function parseRating(ratingStr: string): { rating: number; reviewCount: number } {
  const match = ratingStr.match(/(\d+\.\d+)\((\d+)\)/);
  if (match) {
    return {
      rating: Number.parseFloat(match[1]),
      reviewCount: Number.parseInt(match[2], 10),
    };
  }
  return { rating: 0, reviewCount: 0 };
}

/**
 * Transform raw JSON vendor data to Vendor type
 *
 * @param rawVendor - Raw vendor object from JSON file
 * @returns Transformed Vendor object
 */
function transformVendor(rawVendor: {
  id: string;
  name: string;
  category: string;
  phone: string;
  hours?: string;
  verified?: boolean;
  coordinates?: [number, number];
  responseTime?: string;
  hasInstantBooking?: boolean;
  acceptsOnlinePayment?: boolean;
  minimumCharge?: number;
  insuranceCoverage?: number;
  licenseNumber?: string;
  yearInBusiness?: number;
}): Vendor {
  const { rating, reviewCount } = parseRating(rawVendor.category);
  const extractedCategory = extractCategory({ name: rawVendor.name });

  return {
    id: rawVendor.id,
    name: rawVendor.name,
    category: extractedCategory,
    email: '', // Not available in JSON
    phone: rawVendor.phone,
    address: '', // Not available in JSON
    location: '', // Not available in JSON
    verified: rawVendor.verified || false,
    rating,
    reviewCount,
    services: [extractedCategory],
    specialties: [],
    price: '', // Not available in JSON
    coordinates: rawVendor.coordinates,
    responseTime: rawVendor.responseTime,
    hours: rawVendor.hours,
    hasInstantBooking: rawVendor.hasInstantBooking,
    acceptsOnlinePayment: rawVendor.acceptsOnlinePayment,
    minimumCharge: rawVendor.minimumCharge,
    insuranceCoverage: rawVendor.insuranceCoverage,
    licenseNumber: rawVendor.licenseNumber,
    yearInBusiness: rawVendor.yearInBusiness,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Load vendors from JSON file
 *
 * Reads vendor data from local JSON file and transforms it to Vendor[] format.
 * Used as fallback when API is unavailable.
 *
 * @returns Promise resolving to array of Vendor objects
 */
export async function loadVendorsFromFile(): Promise<Vendor[]> {
  try {
    // Import vendor JSON data (Vite will bundle this)
    const vendorData = await import(
      '../../tests/playwright/rochester-hills-vendors-48306/mobile-app-vendors.json'
    );

    if (!vendorData.vendors || !Array.isArray(vendorData.vendors)) {
      console.error('Invalid vendor data format');
      return [];
    }

    // Transform raw vendor data to Vendor type
    const vendors = vendorData.vendors.map(transformVendor);

    return vendors;
  } catch (error) {
    console.error('Error loading vendors from file:', error);
    return [];
  }
}
